import React, { useEffect, useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ShieldCheck, Award, Sparkles } from "lucide-react";

/**
 * Premium hero visual — split-screen friendly.
 * Right-side cinematic image of pharmacist + floating glass trust pills + lab glow.
 */
export default function HeroVisual() {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const yShift = useTransform(scrollYProgress, [0, 0.4], [0, -80]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const tx1 = useSpring(useTransform(mx, [-1, 1], [-14, 14]), { stiffness: 60, damping: 14 });
  const ty1 = useSpring(useTransform(my, [-1, 1], [-14, 14]), { stiffness: 60, damping: 14 });
  const tx2 = useSpring(useTransform(mx, [-1, 1], [10, -10]), { stiffness: 50, damping: 12 });
  const ty2 = useSpring(useTransform(my, [-1, 1], [8, -8]), { stiffness: 50, damping: 12 });

  useEffect(() => {
    const onMove = (e) => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      mx.set((e.clientX / w) * 2 - 1);
      my.set((e.clientY / h) * 2 - 1);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [mx, my]);

  // Lightweight glow particles
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      canvas.width = canvas.offsetWidth * DPR;
      canvas.height = canvas.offsetHeight * DPR;
    };
    resize();
    window.addEventListener("resize", resize);

    const N = 26;
    const particles = Array.from({ length: N }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: 0.6 + Math.random() * 1.6,
      vx: (Math.random() - 0.5) * 0.12,
      vy: -0.04 - Math.random() * 0.16,
      a: 0.18 + Math.random() * 0.4,
      hue: Math.random() > 0.5 ? "#D9C99B" : "#FFFFFF",
    }));

    const loop = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of particles) {
        p.x += p.vx; p.y += p.vy;
        if (p.y < -10) { p.y = canvas.height + 10; p.x = Math.random() * canvas.width; }
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        ctx.beginPath();
        ctx.fillStyle = p.hue;
        ctx.globalAlpha = p.a;
        ctx.arc(p.x, p.y, p.r * DPR, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <motion.div
      ref={wrapRef}
      style={{ y: yShift }}
      className="relative w-full h-full"
      data-testid="hero-visual"
    >
      {/* Background glow rings */}
      <div className="absolute -top-20 -right-20 w-[520px] h-[520px] rounded-full blur-3xl opacity-50 pointer-events-none"
           style={{ background: "radial-gradient(circle, #C9B47A 0%, transparent 60%)" }} />
      <div className="absolute -bottom-32 -left-20 w-[480px] h-[480px] rounded-full blur-3xl opacity-30 pointer-events-none"
           style={{ background: "radial-gradient(circle, #0F0F0F 0%, transparent 65%)" }} />

      {/* Image frame with cinematic shape */}
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        style={{ x: tx1, y: ty1 }}
        className="relative w-full h-full"
      >
        <div className="relative w-full h-full rounded-[2.5rem] overflow-hidden shadow-[0_60px_120px_-30px_rgba(15,15,15,0.45)]">
          <img
            src="/generated/hero-pharmacist.jpg"
            alt="Farmacêutico manipulando fórmulas no laboratório Biofarmax"
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            data-testid="hero-image"
          />
          {/* Subtle gradient overlay for legibility */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/40 via-transparent to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-[#0A0A0A]/20" />
          {/* Inner border for premium feel */}
          <div className="absolute inset-0 rounded-[2.5rem] ring-1 ring-white/20 pointer-events-none" />
          {/* Canvas particles */}
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none mix-blend-screen opacity-70" />
        </div>

        {/* Top-left glass pill — certified */}
        <motion.div
          initial={{ opacity: 0, y: -20, x: -10 }}
          animate={{ opacity: 1, y: 0, x: 0 }}
          transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          style={{ x: tx2, y: ty2 }}
          className="absolute -top-5 -left-5 glass-card px-5 py-3.5 rounded-2xl flex items-center gap-3 z-10"
        >
          <div className="w-10 h-10 rounded-full bg-brand-primary/10 grid place-items-center">
            <ShieldCheck size={20} className="text-brand-primary" strokeWidth={1.6} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary font-tech">Qualidade</div>
            <div className="font-heading text-lg text-brand-primary leading-none">Certificada</div>
          </div>
        </motion.div>

        {/* Bottom-right glass pill — rating */}
        <motion.div
          initial={{ opacity: 0, y: 20, x: 10 }}
          animate={{ opacity: 1, y: 0, x: 0 }}
          transition={{ duration: 0.9, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
          style={{ x: tx2, y: ty2 }}
          className="absolute -bottom-6 -right-4 sm:right-2 glass-card px-5 py-3.5 rounded-2xl z-10"
        >
          <div className="flex items-center gap-2 mb-1">
            <Award size={16} className="text-brand-accent" strokeWidth={1.6} />
            <span className="font-heading text-lg text-brand-primary leading-none">4.9 / 5.0</span>
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary font-tech">+40.000 clientes</div>
        </motion.div>

        {/* Middle-right floating capsule glow */}
        <motion.div
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.5 }}
          className="absolute top-1/2 -right-8 -translate-y-1/2 w-16 h-16 rounded-full hidden md:grid place-items-center bg-brand-accent text-white shadow-[0_20px_40px_-12px_rgba(201,180,122,0.6)] z-10"
        >
          <Sparkles size={22} strokeWidth={1.6} className="pulse-soft" />
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
